// /src/pages/AdminPage.jsx
import React, { useState } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";

/** ---- helpers ---- **/
function flattenObject(obj, prefix = "", out = {}) {
  if (obj == null) return out;
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      flattenObject(v, key, out);
    } else {
      out[key] = Array.isArray(v) ? v.join("; ") : v;
    }
  }
  return out;
}

function tsToISO(v) {
  if (!v) return "";
  if (typeof v?.toDate === "function") return v.toDate().toISOString();
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString();
}

function toCSV(rows, headers) {
  const escape = (val) =>
    `"${(val ?? "").toString().replace(/"/g, '""').replace(/\n/g, " ")}"`;
  return [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");
}

function download(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** ---- page ---- **/
export default function AdminPage() {
  const [loading, setLoading] = useState(false);

  const getAllUsers = async () => {
    const snap = await getDocs(collection(db, "users"));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  const exportJSON = async () => {
    setLoading(true);
    try {
      const raw = await getAllUsers();
      const normalized = raw.map((u) => {
        // normalize timestamps commonly used
        if (u.createdAt) u.createdAt = tsToISO(u.createdAt);
        if (u.updatedAt) u.updatedAt = tsToISO(u.updatedAt);
        return u;
      });
      const json = JSON.stringify(normalized, null, 2);
      download(
        `users-full-report-${new Date().toISOString().slice(0, 10)}.json`,
        json,
        "application/json;charset=utf-8;"
      );
    } catch (e) {
      console.error(e);
      alert("Export failed. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    setLoading(true);
    try {
      const raw = await getAllUsers();
      const flattened = raw.map((u) => {
        const copy = { ...u };
        copy.createdAt = tsToISO(copy.createdAt);
        copy.updatedAt = tsToISO(copy.updatedAt);
        return flattenObject(copy);
      });
      // collect headers across all rows
      const headers = Array.from(
        flattened.reduce((s, r) => {
          Object.keys(r).forEach((k) => s.add(k));
          return s;
        }, new Set())
      ).sort();

      const csvRows = flattened.map((r) =>
        headers.reduce((acc, h) => ((acc[h] = r[h] ?? ""), acc), {})
      );
      const csv = toCSV(csvRows, headers);
      download(
        `users-full-report-${new Date().toISOString().slice(0, 10)}.csv`,
        csv,
        "text/csv;charset=utf-8;"
      );
    } catch (e) {
      console.error(e);
      alert("Export failed. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-stage" style={{ padding: 16 }}>
      <h2 className="stage-text" style={{ marginTop: 0 }}>
        Admin • Full Users Report
      </h2>
      <div
        className="card glassy"
        style={{ padding: 16, display: "flex", gap: 8, flexWrap: "wrap" }}
      >
        <button className="btn gold" onClick={exportCSV} disabled={loading}>
          {loading ? "Preparing…" : "Download CSV (full users)"}
        </button>
        <button className="btn" onClick={exportJSON} disabled={loading}>
          {loading ? "Preparing…" : "Download JSON (full users)"}
        </button>
      </div>
      <p style={{ opacity: 0.7, marginTop: 8 }}>
        Exports the entire <code>users</code> collection. No filters applied.
      </p>
    </div>
  );
}
